import {
  GridModalFormProps,
  UseFormReturnType,
  Validator,
  globalMessageHandler,
  loadMultipleOptions,
  useForm,
} from "common";
import { useParams } from "common/components/Form/ParamsContext";
import { Dictionary } from "features/dictionaries/dictionaryApi";
import useDictionaries from "features/dictionaries/useDictionaries";
import { getPartnerGroupService } from "features/partner-groups/partnerGroupApi";
import { getUserService } from "features/users/userApi";
import I18n from "i18n";
import { Params } from "react-router-dom";
import { PartnerItem, PartnerOptions, getPartnerService } from "./partnerApi";

export interface UsePartnerFormProps extends GridModalFormProps {}

type RouteParams = {
  partner_id?: string;
};

export interface UsePartnerFormReturnType extends UseFormReturnType<
  PartnerItem,
  PartnerOptions
> {
  params?: Readonly<
    Params<
      "partner_id" | "partner_group_id" | "booking_id" | "booking_option_id"
    >
  >;
  dictionaries: Dictionary;
}

export default function usePartnerForm({
  id,
  afterSave,
  defaultValues,
}: UsePartnerFormProps): UsePartnerFormReturnType {
  const params = useParams<
    "partner_id" | "partner_group_id" | "booking_id" | "booking_option_id"
  >();
  const scope = { "partner-groups": params.partner_group_id };
  const service = getPartnerService(scope);

  function customValidation(newItem: PartnerItem) {
    const commissions = (newItem as any).partner_commissions || [];
    const isCreate = !params.partner_id;
    const rowErrors: Record<number, any> = {};
    let baseCommissionError: any = undefined;
    console.log({ commissions });
    if (!commissions.length) {
      return {
        partner_commissions: {
          message:
            "Hiányzik az alapértelmezett jutalék sor (program és dátum nélkül, Létszám: 1)",
        },
      };
    }

    // Check for base commission row in create mode
    if (isCreate) {
      const hasBaseCommission = commissions.some(
        (commission: any) =>
          (commission.program_id === null ||
            commission.program_id === undefined) &&
          (commission.valid_from === null ||
            commission.valid_from === undefined) &&
          Number(commission.headcount) === 1,
      );

      if (!hasBaseCommission) {
        baseCommissionError = {
          partner_commissions: {
            message:
              "Hiányzik az alapértelmezett jutalék sor (program és dátum nélkül, Létszám: 1)",
          },
        };
      }
    }

    commissions.forEach((commission: any, index: number) => {
      // Skip validation for base row in create mode
      const isBaseRow =
        isCreate &&
        (commission.program_id === null ||
          commission.program_id === undefined) &&
        (commission.valid_from === null ||
          commission.valid_from === undefined) &&
        Number(commission.headcount) === 1;

      if (isBaseRow) {
        // Only validate commission_rate for base row
        if (!commission.commission_rate) {
          rowErrors[index] = {
            commission_rate: "Number is required",
          };
        }
        return;
      }

      // For non-base rows, validate all fields
      const result = Validator.validate(
        {
          //valid_from: Validator.rule.string().required(),
          //program_id: Validator.rule.number().required(),
          headcount: Validator.rule.number().required(),
          commission_rate: Validator.rule.number().required(),
        },
        commission,
        "PartnerCommission",
      );

      if (!result.success) {
        rowErrors[index] = result.errors;
      }
    });

    if (Object.keys(rowErrors).length > 0) {
      console.log({ rowErrors });
      return {
        partner_commissions: {
          message: baseCommissionError
            ? "Hiányzik az alapértelmezett jutalék sor (program és dátum nélkül, Létszám: 1)"
            : undefined,
          ...rowErrors,
        },
      };
    }

    return baseCommissionError;
  }

  const form = useForm<PartnerItem, PartnerOptions>({
    id: params.partner_id || id,
    ignoreNavigateAfterCreate: true,
    translationCategory: "Partner",
    rules: {
      name: Validator.rule.string().required(),
      email: Validator.rule.string().required(),
      phone: Validator.rule.string().required(),
      partner_group_id: params.partner_group_id
        ? Validator.rule.optional()
        : Validator.rule.any().required(),
    },
    afterSave,
    defaultValues,
    customValidation,
    onValidationFailed: () => {
      globalMessageHandler.snack({
        severity: "error",
        message: I18n.t("App.formHasErrors"),
      });
    },
    ...service,
    loadOptions: loadMultipleOptions({
      partner_group_id: () =>
        getPartnerGroupService(undefined).loadAutocomplete(),
      account_manager_id: () => getUserService().loadAutocomplete(),
    }),
  });
  const { dictionaries } = useDictionaries();
  console.log({ errors: form.errors });
  return { ...form, params, dictionaries };
}
